import { useEffect, useMemo, useState, forwardRef } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import { Scale, HelpCircle, Table2, GitBranch, Gavel, Lightbulb, ListChecks, Landmark } from "lucide-react";

/**
 * Slide kinds allowed for display. `timeline` (Key Dates) and `precedents`
 * (Precedents Relied On) are intentionally omitted per product rules.
 */
const KIND_META = {
  title:      { icon: Landmark,   label: "Case at a glance",     tint: "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" },
  bullets:    { icon: ListChecks, label: "Key points",           tint: "bg-secondary text-secondary-foreground" },
  question:   { icon: HelpCircle, label: "The legal question",   tint: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200" },
  comparison: { icon: Table2,     label: "Statutory comparison", tint: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200" },
  flow:       { icon: GitBranch,  label: "The Court's reasoning", tint: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200" },
  verdict:    { icon: Gavel,      label: "The verdict",          tint: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200" },
  takeaway:   { icon: Lightbulb,  label: "Why it matters",       tint: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
};

const FORBIDDEN_KINDS = new Set(["timeline", "precedents"]);

function pick(s, key, lang) {
  const hi = s[`${key}_hi`];
  const en = s[`${key}_en`];
  if (lang === "hi") return (Array.isArray(hi) ? hi.length : hi) ? hi : en;
  return en;
}

function isPartiesSlide(s) {
  const t = String(s?.title_en || "").toLowerCase();
  return (
    (s?.kind === "bullets") &&
    (t.includes("parties") || t.includes("who is who") || t.includes("who's who"))
  );
}

/**
 * Renders one dossier slide. Exported (and forwardRef'd) so the admin panel can
 * render it off-screen and export it as an image for social sharing.
 *
 * Sizing:
 *   Mobile — aspect-[4/5] (tall portrait) so the whole slide is visible without
 *   dead space or overflow.
 *   Desktop — aspect-[16/9] wide slide.
 */
export const DossierSlide = forwardRef(function DossierSlide({ slide: s, index, total, caseTitle, citation, date, lang = "en", className }, ref) {
  const kind = KIND_META[s.kind] ? s.kind : "bullets";
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  const title = pick(s, "title", lang) || meta.label;
  const subtitle = pick(s, "subtitle", lang);
  const bullets = pick(s, "bullets", lang) || [];
  const headers = pick(s, "table_headers", lang) || [];
  const rows = pick(s, "table_rows", lang) || [];
  const callout = pick(s, "callout", lang);

  return (
    <div
      ref={ref}
      className={cn(
        "dossier-slide relative flex w-full flex-col overflow-hidden rounded-2xl border bg-[#faf8f3] p-5 text-[#1b1b1b] shadow-lg dark:bg-[#f6f3ec]",
        "aspect-[4/5] sm:aspect-[16/9] sm:p-8",
        className,
      )}
      data-testid={`dossier-slide-${index}`}
    >
      {/* Paper grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:radial-gradient(#000_0.6px,transparent_0.6px)] [background-size:6px_6px]" />

      {/* Header strip */}
      <div className="relative flex items-center justify-between gap-3 border-b border-[#1b1b1b]/15 pb-2.5 sm:pb-3">
        <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider sm:text-xs", meta.tint)}>
          <Icon className="h-3.5 w-3.5" /> {meta.label}
        </span>
        <span className="font-mono text-[11px] text-[#1b1b1b]/60 sm:text-xs">{index + 1} / {total}</span>
      </div>

      {/* Body — scrolls only if truly overflowing, and text is legibly sized on mobile. */}
      <div className="relative mt-3 flex-1 overflow-y-auto pr-1 sm:mt-4 sm:overflow-hidden">
        {kind === "title" ? (
          <div className="flex h-full flex-col justify-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1b1b1b]/55 sm:text-xs">Supreme Court of India</p>
            <h3 className="mt-2 font-serif text-2xl font-bold leading-tight sm:text-4xl">{title}</h3>
            {subtitle && <p className="mt-3 max-w-[95%] text-[15px] leading-snug text-[#1b1b1b]/80 sm:text-base">{subtitle}</p>}
            {bullets.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2 sm:mt-5">
                {bullets.map((b, i) => (
                  <span key={i} className="rounded-md border border-[#1b1b1b]/20 bg-white/70 px-2.5 py-1 text-[12px] font-medium sm:text-xs">{b}</span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <h3 className="font-serif text-[19px] font-bold leading-tight sm:text-2xl">{title}</h3>
            {subtitle && <p className="mt-1 text-[13px] leading-snug text-[#1b1b1b]/70 sm:text-sm">{subtitle}</p>}

            {kind === "comparison" && headers.length > 0 ? (
              <div className="mt-3 overflow-x-auto rounded-lg border border-[#1b1b1b]/20 sm:mt-4">
                <table className="w-full text-left text-[13px] sm:text-sm">
                  <thead className="bg-[#1b1b1b]/[0.06]">
                    <tr>{headers.map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.map((r, ri) => (
                      <tr key={ri} className="border-t border-[#1b1b1b]/10">
                        {(r || []).map((c, ci) => <td key={ci} className={cn("px-3 py-2 align-top leading-snug", ci === 0 && "font-semibold")}>{c}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : kind === "flow" ? (
              <ol className="mt-3 space-y-2.5 sm:mt-4">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] leading-snug sm:text-[15px]">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 font-mono text-[11px] font-bold text-white">{i + 1}</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <ul className="mt-3 space-y-2.5 sm:mt-4">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] leading-snug sm:text-[15px]">
                    <Scale className="mt-0.5 h-4 w-4 shrink-0 text-[#1b1b1b]/55" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {callout && (
        <div className={cn(
          "relative mt-3 rounded-lg border-l-4 px-3.5 py-2.5 text-[13px] font-semibold leading-snug sm:text-sm",
          kind === "verdict" ? "border-rose-600 bg-rose-50 text-rose-900" : "border-amber-500 bg-amber-50 text-amber-900",
        )}>
          {callout}
        </div>
      )}

      {/* Footer */}
      <div className="relative mt-3 flex items-center justify-between gap-3 border-t border-[#1b1b1b]/15 pt-2.5 text-[11px] text-[#1b1b1b]/65 sm:text-xs">
        <span className="truncate font-medium">{caseTitle}{citation ? ` · ${citation}` : ""}</span>
        <span className="shrink-0 font-mono">{date || "litigationindia.com"}</span>
      </div>
    </div>
  );
});

export const JudgmentCarousel = ({ slides = [], caseTitle, citation, date }) => {
  const { lang, t } = useLanguage();
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  // Defence-in-depth: even if legacy DB rows contain forbidden slide kinds
  // (timeline / precedents / parties recap), never render them.
  const filtered = useMemo(
    () => (slides || []).filter((s) => s && !FORBIDDEN_KINDS.has(s.kind) && !isPartiesSlide(s)),
    [slides],
  );

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api]);

  if (!filtered.length) return null;

  return (
    <div data-testid="judgment-carousel">
      <Carousel setApi={setApi} opts={{ align: "start", loop: false }} className="w-full">
        <CarouselContent className="-ml-2 sm:-ml-4">
          {filtered.map((s, i) => (
            <CarouselItem key={i} className="basis-full pl-2 sm:pl-4">
              {s.image ? (
                <div className="relative flex w-full items-center justify-center overflow-hidden rounded-2xl border bg-muted shadow-lg aspect-[4/5] sm:aspect-[16/9]">
                  <img src={s.image} alt={(lang === "hi" ? s.title_hi : s.title_en) || `${caseTitle} slide ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} decoding="async" width={1280} height={720} className="h-full w-full object-contain" />
                  <div className="absolute right-4 top-4 rounded-full bg-black/50 px-3 py-1 text-xs font-mono text-white">{i + 1} / {filtered.length}</div>
                </div>
              ) : (
                <DossierSlide slide={s} index={i} total={filtered.length} caseTitle={caseTitle} citation={citation} date={date} lang={lang} />
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious data-testid="judgment-carousel-prev" className="left-2" />
        <CarouselNext data-testid="judgment-carousel-next" className="right-2" />
      </Carousel>

      <div className="mt-4 flex items-center justify-center gap-2">
        {Array.from({ length: count }).map((_, i) => (
          <button
            key={i}
            aria-label={`${t("slide")} ${i + 1}`}
            data-testid={`judgment-carousel-dot-${i}`}
            onClick={() => api && api.scrollTo(i)}
            className={cn("h-2 rounded-full transition-[width,background-color]", current === i ? "w-6 bg-primary" : "w-2 bg-primary/30 hover:bg-primary/50")}
          />
        ))}
      </div>
    </div>
  );
};
